// Libraries
import { useMemo } from 'react';
import { Flex, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import { useRouter } from 'next/router';
// Components
import { FilterOption } from '@organisms/index';
import { TableFiltersButton, TableFiltersDrawer, TableFiltersHidden, TableFiltersVisible } from './Components';
import { getFiltersGroups } from './TableFilters.helpers';
// Types
import { useUnitAndRangeStore } from '@stores/index';
import { TableFiltersProps } from './TableFilters.types';

/**
 * The purpose of this array is to hide the filters included in it.
 */
export const excludedFilters = ['vesselName', 'scheduleName'];

/**
 * TableFilters component.
 *
 * @component
 * @template TableBody - The type of data in the table body.
 * @template ColumnNames - The type of column names.
 * @param {ableFiltersProps<TableBody, ColumnNames>} props - The component props.
 * @param {Table<TableBody>} props.table - The table instance.
 * @param {FilterOptionsMap<ColumnNames>} props.filterOptionsMap - The map of column IDs to filter options.
 * @param {boolean} props.disableVisible - Flag to disable the filters visible layout and only show them in the lateral drawer.
 * @returns {ReactElement} - The JSX element representing the TableFilters component.
 */
export const TableFilters = <TableBody, ColumnNames extends string>({
  table,
  filterOptionsMap,
  disableVisible = false,
  disableFilters = false,
}: TableFiltersProps<TableBody, ColumnNames>) => {
  const router = useRouter();
  const { hasRangePill } = useUnitAndRangeStore();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const hideFilters = useBreakpointValue({ base: true, xl: false }, { ssr: false });
  const smallerViews = useBreakpointValue({
    base: true,
    md: false,
  });
  const filterOptions = Object.values(filterOptionsMap) as FilterOption[];
  const columnFilters = table.getState().columnFilters;
  const appliedFilters = columnFilters.reduce((acc, curr) => {
    let filterCount = (curr.value as string[] | null)?.length ?? 0;
    if (excludedFilters.includes(curr.id)) {
      return acc;
    }
    if (curr.id === 'capacityAndUnit') {
      filterCount = hasRangePill ? 2 : 1;
    }
    return acc + filterCount;
  }, 0);

  // NOTE: We only need to get the initial instance of the table.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { hidden, visible } = useMemo(() => getFiltersGroups(table, filterOptions), [filterOptionsMap]);
  const flexWrap = smallerViews ? 'wrap' : 'initial';

  /**
   * Function used to clear all filters from the clear filters button
   *
   * @function
   * @returns void
   */
  const clearFilters = () => {
    router.replace(router.pathname);
    table.setColumnFilters([]);
  };

  const displayFilters = (hideFilters || disableVisible) && !disableFilters;

  return displayFilters ? (
    <Flex gap={3} alignItems='end' flexWrap={flexWrap}>
      <TableFiltersVisible filterOptionsMap={filterOptionsMap} columnsGroup={visible} smallerViews={smallerViews} />
      <TableFiltersButton onClick={onOpen} appliedFilters={appliedFilters} smallerViews={smallerViews} />
      <TableFiltersDrawer isOpen={isOpen} onClose={onClose} appliedFilters={appliedFilters} clearFilters={clearFilters}>
        <TableFiltersHidden filterOptionsMap={filterOptionsMap} columnsGroup={hidden} smallerViews={smallerViews} />
      </TableFiltersDrawer>
    </Flex>
  ) : (
    <Flex align='end' gap={5}>
      <TableFiltersVisible filterOptionsMap={filterOptionsMap} columnsGroup={{ ...visible, ...hidden }} />
    </Flex>
  );
};
